import { List, OrderedMap, Record } from 'immutable';
import { difference } from 'lodash';

import Product from './Product';
import notify from '../lib/notify';
import I18n from '../lib/i18n';

const defaultValue: {
  items: OrderedMap<string, Product>; // nsuidがキー
  selectedIds: List<string>;
  isFetching: boolean;
  hasSuperset: boolean;
} = {
  items: OrderedMap(),
  selectedIds: List(),
  isFetching: true,
  hasSuperset: false,
};

export default class ProductList extends Record(defaultValue) {
  public validProduct(product: Product, countries: string[], platforms: string[]): boolean {
    const availableCountries = product.available_countries === undefined ? [] : product.available_countries;
    const availableCountriesNintendoSwitch2 = product.available_countries_nintendo_switch_2 === undefined ? [] : product.available_countries_nintendo_switch_2;

    if (platforms.length === 2) {
      // 選択しているプラットフォームが Switch かつ Switch2 の場合
      return difference(countries, availableCountries).length === 0 && difference(countries, availableCountriesNintendoSwitch2).length === 0;
    } else if (platforms[0] !== 'nx') {
      // 選択しているプラットフォームが Switch2 のみの場合
      return difference(countries, availableCountriesNintendoSwitch2).length === 0;
    } else {
      // 選択しているプラットフォームが Switch のみ または チェックなしの場合
      // チェックなしの場合は以前と同様に availableCountries のみで判定する
      return difference(countries, availableCountries).length === 0;
    }
  }
  public selectLimitExceeded(maxProductsLength: number): boolean {
    return this.selectedIds.size >= maxProductsLength;
  }
  public getName(nsUid: string): string {
    const product = this.items.get(nsUid);
    if (product === undefined) { return ''; }
    return product.availableName();
  }
  public getItem(id: string) {
    return this.items.get(id, new Product({ ns_uid: id, valid: false, product_names: {} }));
  }
  public filterSelected() {
    // Productが非同期にセットされるため
    if (this.items.size === 0) { return List(); }
    return this.selectedIds.map(id => this.getItem(id));
  }
  public filterSelectedWithOrder() {
    // Productが非同期にセットされるため
    if (this.items.size === 0) { return List(); }
    return this.selectedIds.map(id => Object({ index: this.selectedIds.keyOf(id), product: this.getItem(id) }));
  }
  public filterNotSelected() {
    return this.items.filter(product => !this.selectedIds.includes(product.ns_uid)).valueSeq();
  }
  public filterAvailableCountries(countries: string[], platforms: string[]) {
    return this
      .filterNotSelected()
      .filter(product => this.validProduct(product, countries, platforms))
      .map(product => product.toSelectOption()).toJS();
  }
  public filterAvailableCountriesOnlySoftDetail(countries: string[], platforms: string[]) {
    return this.items
      .filter(product => Product.isSoftDetail(product.ns_uid)).valueSeq()
      .filter(product => this.validProduct(product, countries, platforms))
      .map(product => product.toSelectOption()).toJS();
  }
  public getSelectedMaxLength() {
    return this.filterSelected().map(product => product.availableName().length).max();
  }
  public productListLimitExceeded(maxLength: number, title: string ) {
    if (this.selectLimitExceeded(maxLength)){
      notify({
        title: `${title}: `,
        message: I18n.t('activerecord.errors.product.limit_product_number'),
        type: 'danger',
      });
      return true;
    };
    return false;
  }
}
